// What an error does, decided once for every page (module-10 Section 1.3): TanStack Query's QueryCache and
// MutationCache handlers, not each page. Pages only render <ErrorState> for their own reads.
//   401                    reads and writes: a full-page /sign-in?returnTo=<this page> (REQ-18.2)
//   409 revision_conflict  writes: "This ticket has changed", and the ticket, the queue and the dashboard reload.
//                          Never retried: no write is (a stale page must change nothing it no longer shows)
//   anything else          reads: retried once, never a 4xx, then the page's <ErrorState>
//                          writes: a toast with the server's friendly detail
// A mutation with meta.silent shows its own errors (the reporter commands, M11's registration wizard); a 401
// still goes to sign-in.
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { isApiError, isRevisionConflict } from './api';
import { invalidateTickets } from './ticket-commands';

/** Reads are retried once, except answers that won't change on retry (4xx). */
export const MAX_READ_RETRIES = 1;

export const CONFLICT_TITLE = 'This ticket has changed';
export const CONFLICT_DESCRIPTION = 'Someone else updated it, so we’ve reloaded it. Check it and try again.';
const WRITE_FALLBACK = 'Something went wrong. Try again.';

/** The browser effects the policy has; main.tsx passes the real ones, the L1 test fakes. */
export interface ErrorEffects {
  /** A full-page navigation to sign-in, returning to the current page. */
  signIn: () => void;
  toastError: (message: string, description?: string) => void;
}

/** `retry` for every read: never a 4xx (403, 404, 410 won't change), else once more. */
export function shouldRetryRead(failureCount: number, error: unknown): boolean {
  const clientError = isApiError(error) && error.status >= 400 && error.status < 500;
  return !clientError && failureCount < MAX_READ_RETRIES;
}

export function handleReadError(error: unknown, effects: ErrorEffects): void {
  if (isApiError(error) && error.status === 401) {
    effects.signIn();
  }
}

export function handleWriteError(
  error: unknown,
  meta: { silent?: boolean } | undefined,
  effects: ErrorEffects,
  reloadTickets: () => Promise<void>,
): void {
  if (isApiError(error) && error.status === 401) {
    effects.signIn();
    return;
  }
  if (meta?.silent) {
    return;
  }
  if (isRevisionConflict(error)) {
    effects.toastError(CONFLICT_TITLE, CONFLICT_DESCRIPTION);
    void reloadTickets();
    return;
  }
  effects.toastError(isApiError(error) ? error.detail : WRITE_FALLBACK);
}

/** The SPA's one QueryClient, with the error policy above. */
export function createQueryClient(effects: ErrorEffects): QueryClient {
  const queryClient: QueryClient = new QueryClient({
    queryCache: new QueryCache({ onError: (error) => handleReadError(error, effects) }),
    mutationCache: new MutationCache({
      onError: (error, _vars, _context, mutation) =>
        handleWriteError(error, mutation.meta, effects, () => invalidateTickets(queryClient)),
    }),
    defaultOptions: {
      queries: { refetchOnWindowFocus: true, retry: shouldRetryRead },
      mutations: { retry: false },
    },
  });
  return queryClient;
}
